/**
 * Transaction History Component
 * Shows user's classification history, grouping rows from the same bulk upload.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Edit as DirectIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  FolderZip as BatchIcon,
  PlayArrow as ResumeIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { TransactionHistory as Transaction } from '../../../services/tariffClassifierPageService';

interface TransactionHistoryProps {
  transactions: Transaction[];
  loading?: boolean;
  onRefresh: () => void;
  onViewDetails: (transactionId: string) => void;
  onDownload: (transactionId: string) => void;
  // Download all results in a bulk batch as a ZIP. Called with the child transaction IDs.
  onDownloadBatch?: (transactionIds: string[]) => void;
  // Download all results in a bulk batch as a single merged CSV or Excel file.
  onDownloadMergedBatch?: (transactionIds: string[], format: 'csv' | 'excel') => void;
  // Resume mapping for a transaction stuck in 'awaiting_mapping' status.
  onResumeMapping?: (transactionId: string) => void;
  // ID of the transaction whose mapping preview is currently loading (shows a
  // spinner instead of the ▶ icon on that row).
  resumingMappingId?: string | null;
}

type HistoryEntry =
  | { kind: 'single'; transaction: Transaction }
  | { kind: 'group'; bulkId: string; transactions: Transaction[] };

// Group consecutive transactions that share a bulk_transaction_id. Backend returns
// rows sorted by created_at DESC; child transactions from one bulk upload share a
// created_at within milliseconds so they cluster together naturally.
//
// Single-child groups (a bulk upload that happened to contain one file) are
// demoted to flat rows — the "Batch · 1 files" parent row adds no information.
function groupTransactions(transactions: Transaction[]): HistoryEntry[] {
  const groups = new Map<string, Transaction[]>();
  const order: HistoryEntry[] = [];

  for (const t of transactions) {
    if (t.bulk_transaction_id) {
      let arr = groups.get(t.bulk_transaction_id);
      if (!arr) {
        arr = [];
        groups.set(t.bulk_transaction_id, arr);
        order.push({ kind: 'group', bulkId: t.bulk_transaction_id, transactions: arr });
      }
      arr.push(t);
    } else {
      order.push({ kind: 'single', transaction: t });
    }
  }

  return order.map((entry) =>
    entry.kind === 'group' && entry.transactions.length === 1
      ? { kind: 'single', transaction: entry.transactions[0] }
      : entry
  );
}

const getStatusColor = (
  status: string
): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'primary';
    case 'pending':
      return 'warning';
    case 'awaiting_mapping':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getModeIcon = (mode: string) => {
  if (mode === 'file_upload') return <UploadIcon fontSize="small" />;
  return <DirectIcon fontSize="small" />;
};

const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch {
    return dateString;
  }
};

const TransactionRow: React.FC<{
  transaction: Transaction;
  indented?: boolean;
  onViewDetails: (id: string) => void;
  onDownload: (id: string) => void;
  onResumeMapping?: (id: string) => void;
  resumingMappingId?: string | null;
}> = ({
  transaction,
  indented = false,
  onViewDetails,
  onDownload,
  onResumeMapping,
  resumingMappingId,
}) => {
  const successRate =
    transaction.total_items && transaction.successful_items
      ? ((transaction.successful_items / transaction.total_items) * 100).toFixed(0)
      : '-';

  return (
    <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
      <TableCell sx={{ pl: indented ? 6 : 2 }}>
        <Typography variant="body2">{formatDate(transaction.created_at)}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          icon={getModeIcon(transaction.classification_mode)}
          label={
            transaction.classification_mode === 'file_upload' ? 'File Upload' : 'Direct'
          }
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
          {transaction.uploaded_file_name || 'Direct classification'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight={600}>
          {transaction.total_items || 1}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {transaction.successful_items !== null && transaction.total_items !== null ? (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {successRate}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {transaction.successful_items}/{transaction.total_items}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            -
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        {transaction.average_confidence !== null && transaction.average_confidence !== undefined ? (
          <Chip
            label={`${(transaction.average_confidence * 100).toFixed(0)}%`}
            size="small"
            color={
              transaction.average_confidence >= 0.9
                ? 'success'
                : transaction.average_confidence >= 0.75
                ? 'warning'
                : 'error'
            }
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            -
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={transaction.status}
          size="small"
          color={getStatusColor(transaction.status)}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(transaction.id);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {transaction.status === 'awaiting_mapping' && onResumeMapping && (
            <Tooltip title="Resolve column mapping">
              <span>
                {/* span wrapper keeps Tooltip happy when IconButton is disabled */}
                <IconButton
                  size="small"
                  color="warning"
                  disabled={resumingMappingId === transaction.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResumeMapping(transaction.id);
                  }}
                >
                  {resumingMappingId === transaction.id ? (
                    <CircularProgress size={16} color="warning" />
                  ) : (
                    <ResumeIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {transaction.status === 'completed' && (
            <Tooltip title="Download Results">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(transaction.id);
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

const BatchGroupRow: React.FC<{
  bulkId: string;
  transactions: Transaction[];
  onViewDetails: (id: string) => void;
  onDownload: (id: string) => void;
  onDownloadBatch?: (transactionIds: string[]) => void;
  onDownloadMergedBatch?: (transactionIds: string[], format: 'csv' | 'excel') => void;
  onResumeMapping?: (id: string) => void;
  resumingMappingId?: string | null;
}> = ({
  bulkId,
  transactions,
  onViewDetails,
  onDownload,
  onDownloadBatch,
  onDownloadMergedBatch,
  onResumeMapping,
  resumingMappingId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [mergedAnchor, setMergedAnchor] = useState<HTMLElement | null>(null);

  const totalItems = transactions.reduce((sum, t) => sum + (t.total_items || 0), 0);
  const successItems = transactions.reduce((sum, t) => sum + (t.successful_items || 0), 0);
  const successRate = totalItems > 0 ? ((successItems / totalItems) * 100).toFixed(0) : '-';

  const confidences = transactions
    .map((t) => t.average_confidence)
    .filter((c): c is number => c !== null && c !== undefined);
  const avgConfidence =
    confidences.length > 0
      ? confidences.reduce((sum, c) => sum + c, 0) / confidences.length
      : null;

  // Group is "completed" only if every child is
  const allCompleted = transactions.every((t) => t.status === 'completed');
  const groupStatus = transactions.some((t) => t.status === 'failed')
    ? 'failed'
    : transactions.some((t) => t.status === 'awaiting_mapping')
    ? 'awaiting_mapping'
    : allCompleted
    ? 'completed'
    : 'processing';

  // Use the latest child's timestamp as the group timestamp
  const latestCreatedAt = transactions[0]?.created_at;

  const completedIds = transactions
    .filter((t) => t.status === 'completed')
    .map((t) => t.id);

  return (
    <>
      <TableRow
        sx={{
          bgcolor: 'primary.50',
          '&:hover': { bgcolor: 'primary.100' },
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
            <Typography variant="body2" fontWeight={600}>
              {formatDate(latestCreatedAt)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            icon={<BatchIcon fontSize="small" />}
            label={`Batch · ${transactions.length} files`}
            size="small"
            color="primary"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            Batch <code>{bulkId.slice(0, 8)}</code>
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight={600}>
            {totalItems}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {totalItems > 0 ? (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {successRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {successItems}/{totalItems}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {avgConfidence !== null ? (
            <Chip
              label={`${(avgConfidence * 100).toFixed(0)}%`}
              size="small"
              color={
                avgConfidence >= 0.9
                  ? 'success'
                  : avgConfidence >= 0.75
                  ? 'warning'
                  : 'error'
              }
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip label={groupStatus} size="small" color={getStatusColor(groupStatus)} />
        </TableCell>
        <TableCell align="center">
          {completedIds.length > 0 && (onDownloadMergedBatch || onDownloadBatch) && (
            <>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMergedAnchor(e.currentTarget);
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={mergedAnchor}
                open={Boolean(mergedAnchor)}
                onClose={() => setMergedAnchor(null)}
                onClick={(e) => e.stopPropagation()}
              >
                {onDownloadMergedBatch && (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setMergedAnchor(null);
                      onDownloadMergedBatch(completedIds, 'csv');
                    }}
                  >
                    <Box>
                      <Typography variant="body2">All in one CSV</Typography>
                      <Typography variant="caption" color="text.secondary">
                        One row per item, source_file column
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
                {onDownloadMergedBatch && (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setMergedAnchor(null);
                      onDownloadMergedBatch(completedIds, 'excel');
                    }}
                  >
                    <Box>
                      <Typography variant="body2">All in one Excel</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Same data as CSV, .xlsx workbook
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
                {onDownloadBatch && (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setMergedAnchor(null);
                      onDownloadBatch(completedIds);
                    }}
                  >
                    <Box>
                      <Typography variant="body2">Separate files (ZIP)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        One CSV per uploaded file
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </TableCell>
      </TableRow>
      {expanded &&
        transactions.map((t) => (
          <TransactionRow
            key={t.id}
            transaction={t}
            indented
            onViewDetails={onViewDetails}
            onDownload={onDownload}
            onResumeMapping={onResumeMapping}
            resumingMappingId={resumingMappingId}
          />
        ))}
    </>
  );
};

const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
  loading = false,
  onRefresh,
  onViewDetails,
  onDownload,
  onDownloadBatch,
  onDownloadMergedBatch,
  onResumeMapping,
  resumingMappingId,
}) => {
  const entries = useMemo(() => groupTransactions(transactions), [transactions]);

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading history...
        </Typography>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Alert severity="info">
        No classification history yet. Start by classifying products above!
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Classification History
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading} size="small">
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>File / Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Items
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Success Rate
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Avg. Confidence
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) =>
              entry.kind === 'single' ? (
                <TransactionRow
                  key={entry.transaction.id}
                  transaction={entry.transaction}
                  onViewDetails={onViewDetails}
                  onDownload={onDownload}
                  onResumeMapping={onResumeMapping}
                  resumingMappingId={resumingMappingId}
                />
              ) : (
                <BatchGroupRow
                  key={entry.bulkId}
                  bulkId={entry.bulkId}
                  transactions={entry.transactions}
                  onViewDetails={onViewDetails}
                  onDownload={onDownload}
                  onDownloadBatch={onDownloadBatch}
                  onDownloadMergedBatch={onDownloadMergedBatch}
                  onResumeMapping={onResumeMapping}
                  resumingMappingId={resumingMappingId}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TransactionHistory;
